import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { RootPortal } from '../common/RootPortal';
import { ImageUtils } from '../../utils/ImageUtils';
import { infoStore } from '../../store/infoStore';

export const NumberPad = ({
  visiblePad,
  close,
  showUsedTicket,
  setPassword,
  password,
}: {
  visiblePad: boolean;
  close: () => void;
  showUsedTicket: () => void;
  setPassword: (password: string) => void;
  password: string;
}) => {
  const { info } = infoStore();
  const [error, setError] = useState<boolean>(false);
  const maxLength = 4;

  const handleNumberClick = (num: number) => {
    if (password.length < maxLength) {
      setPassword(password + num);
    }
  };

  const handleDelete = () => {
    if (password.length > 0) {
      setPassword(password.slice(0, -1));
    }
  };

  useEffect(() => {
    if (password.length === maxLength) {
      if (password !== info.ticketPassword) {
        setError(true);
        setPassword('');
        return;
      }
      close();
      setPassword('');
      showUsedTicket();
    }
  }, [close, info.ticketPassword, password, showUsedTicket, setPassword]);

  return (
    <RootPortal>
      <ModalWrap $visible={visiblePad}>
        <Dim />
        <ModalContent $visible={visiblePad}>
          <Close src={ImageUtils.CloseWhite} alt="Close" onClick={close}></Close>
          <EmptyTop />
          <PasswordCard>
            <Title $isError={error}>
              {error ? `잘못된 패스워드입니다. \n다시 입력해주세요.` : '패스워드를 입력해주세요.'}
            </Title>
            <PasswordDisplay>
              {[...Array(maxLength)].map((_, index) => (
                <PasswordDot key={index} $filled={index < password.length}>
                  {index < password.length && <FilledDot />}
                </PasswordDot>
              ))}
            </PasswordDisplay>
          </PasswordCard>

          <NumberWrap>
            <NumPadContainer>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <NumButton
                  key={num}
                  onClick={() => {
                    setError(false);
                    handleNumberClick(num);
                  }}
                >
                  {num}
                </NumButton>
              ))}
              <EmptyButton />
              <NumButton className="zero" onClick={() => handleNumberClick(0)}>
                0
              </NumButton>
              <EmptyButton $noRight={true} onClick={handleDelete}>
                <img src={ImageUtils.DeleteIcon} alt="Delete" />
              </EmptyButton>
            </NumPadContainer>
          </NumberWrap>
        </ModalContent>
      </ModalWrap>
    </RootPortal>
  );
};

const Close = styled.img`
  position: absolute;
  top: 15px;
  right: 15px;
`;

const ModalWrap = styled.div<{ $visible: boolean }>`
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000000;
`;

const Dim = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
`;

const ModalContent = styled.div<{ $visible: boolean }>`
  width: 100%;
  height: 100%;
  box-shadow: 0px 1px 1px 0px rgba(255, 255, 255, 0.2) inset;
  position: relative;
  z-index: 2;
  border-radius: 20px 20px 0px 0px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const PasswordCard = styled.div`
  margin: 0 auto;
  width: 300px;
  background: #302e2d;
  border-radius: 20px;
  padding: 0px 35px;
  margin-bottom: 30px;
  box-shadow: 0 1px 1px 0 rgba(255, 255, 255, 0.3) inset;
  padding-top: 30px;
  padding-bottom: 50px;
`;

const Title = styled.h2<{ $isError: boolean }>`
  color: #eee;

  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: ${({ $isError }) => ($isError ? '30px' : '42px')}; /* 262.5% */
  letter-spacing: -0.32px;
  margin-bottom: 10px;
  word-wrap: break-word;
  white-space: pre-line;
`;

const PasswordDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const PasswordDot = styled.div<{ $filled: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 7px;
  background: #474747;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FilledDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #eee;
`;

const NumberWrap = styled.div`
  background-color: #d1d5db;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NumPadContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: 10px;
  max-width: 370px;
`;

const EmptyTop = styled.div`
  height: 208px;
  width: 100%;
`;

const NumButton = styled.button`
  width: 110px;
  height: 42px;

  color: #000;

  text-align: center;
  font-family: 'Noto Sans';
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 83.333% */
  letter-spacing: -0.48px;

  border-radius: 4px;
  background: #fff;

  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.1);
  margin-right: 5px;
  margin-bottom: 5px;

  &:nth-child(3n) {
    margin-right: 0;
  }

  @media screen and (max-width: 370px) {
    width: calc(33.33333% - 5px);
  }
`;

const EmptyButton = styled.div<{ $noRight?: boolean }>`
  width: 110px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${(props) => (props.$noRight ? '0' : '5px')};
  @media screen and (max-width: 370px) {
    width: calc(33.33333% - 5px);
  }
`;
